import api from '../../../services/api';

// Single home for the "Koszty stałe" (company fixed costs) domain contract.
// Backend: GET/POST/PUT/DELETE /api/company-costs (+ /templates), gated on
// the financial-reports permission. See AGENTS.md financial-visibility rules
// — this module is a straight pass-through to that backend contract.
//
// NAMING MAP (grep both terms): the backend calls this domain `companyCost*`
// (`/api/company-costs`, companyCostService/Repository), while the operator-
// facing frontend calls it `fixedCosts` (pages/FixedCosts, i18n namespace
// `fixedCosts`). The UI surface is the "Koszty stałe" tab of Rentowność
// (`/app/profitability?tab=fixed-costs`; the legacy `/app/fixed-costs` route
// only redirects there). This module is the bridge between the two names.

// Mirrors backend companyCostService.MAX_COMPANY_COST_AMOUNT — the server
// rejects amounts above this, so the form must too.
export const MAX_COMPANY_COST_AMOUNT = 1000000000;

export const COMPANY_COST_CATEGORIES = [
  'LEASING',
  'OFFICE',
  'UTILITIES',
  'SALARIES',
  'INSURANCE',
  'ACCOUNTING',
  'SUBSCRIPTIONS',
  'OTHER',
] as const;

export type CompanyCostCategory = typeof COMPANY_COST_CATEGORIES[number];

export const COMPANY_COST_CURRENCIES = ['PLN', 'EUR', 'USD'] as const;

export type CompanyCostCurrency = typeof COMPANY_COST_CURRENCIES[number];

export type CompanyCostSource = 'RECURRING' | 'ONE_TIME' | 'KSEF';

export interface CompanyCostEntry {
  id: string;
  month: string;
  templateId: string | null;
  name: string;
  category: CompanyCostCategory;
  amount: number;
  originalCurrency: string | null;
  originalAmount: number | null;
  source: CompanyCostSource;
  invoiceNumber: string | null;
  isPaid: boolean;
  paymentDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyCostTemplate {
  id: string;
  name: string;
  category: CompanyCostCategory;
  amount: number;
  originalCurrency: string | null;
  originalAmount: number | null;
  activeFrom: string;
  activeTo: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyCostsSummary {
  total: number;
  byCategory: Partial<Record<CompanyCostCategory, number>>;
  entryCount: number;
}

export interface CompanyCostsMonthResponse {
  month: string;
  entries: CompanyCostEntry[];
  summary: CompanyCostsSummary;
}

export interface CompanyCostEntryPayload {
  month: string;
  name: string;
  category: CompanyCostCategory;
  amount: number;
  currency?: CompanyCostCurrency;
  invoiceNumber?: string | null;
  isPaid?: boolean;
  paymentDate?: string | null;
  notes?: string | null;
}

export type CompanyCostEntryUpdatePayload = Omit<CompanyCostEntryPayload, 'month'>;

export interface CompanyCostTemplatePayload {
  name: string;
  category: CompanyCostCategory;
  amount: number;
  currency?: CompanyCostCurrency;
  activeFrom: string;
  activeTo?: string | null;
  notes?: string | null;
}

export interface CompanyCostTemplateUpdatePayload extends Partial<CompanyCostTemplatePayload> {
  isActive?: boolean;
}

function normalizeMonthResponse(data: any): CompanyCostsMonthResponse {
  const summary = data?.summary ?? {};
  return {
    month: data?.month ?? '',
    entries: Array.isArray(data?.entries) ? data.entries : [],
    summary: {
      total: Number(summary.total) || 0,
      byCategory: summary.byCategory ?? {},
      entryCount: Number(summary.entryCount) || 0,
    },
  };
}

export async function fetchCompanyCostsMonth(month: string): Promise<CompanyCostsMonthResponse> {
  const response = await api.get('/company-costs', { params: { month } });
  return normalizeMonthResponse(response.data);
}

export async function createCompanyCostEntry(payload: CompanyCostEntryPayload): Promise<CompanyCostEntry> {
  const response = await api.post('/company-costs', payload);
  return response.data;
}

export async function updateCompanyCostEntry(
  id: string,
  payload: CompanyCostEntryUpdatePayload,
): Promise<CompanyCostEntry> {
  const response = await api.put(`/company-costs/${id}`, payload);
  return response.data;
}

export async function deleteCompanyCostEntry(id: string): Promise<void> {
  await api.delete(`/company-costs/${id}`);
}

export async function fetchCompanyCostTemplates(): Promise<CompanyCostTemplate[]> {
  const response = await api.get('/company-costs/templates');
  return Array.isArray(response.data) ? response.data : [];
}

export async function createCompanyCostTemplate(
  payload: CompanyCostTemplatePayload,
): Promise<CompanyCostTemplate> {
  const response = await api.post('/company-costs/templates', payload);
  return response.data;
}

export async function updateCompanyCostTemplate(
  id: string,
  payload: CompanyCostTemplateUpdatePayload,
): Promise<CompanyCostTemplate> {
  const response = await api.put(`/company-costs/templates/${id}`, payload);
  return response.data;
}

export async function deleteCompanyCostTemplate(id: string): Promise<void> {
  await api.delete(`/company-costs/templates/${id}`);
}
